import { SegmentPageContent } from "@/types/marketing/segment-page";

export const BUSINESS_SEGMENT_PAGE: SegmentPageContent = {
  slug: "business",
  title: "Help Genie for Business",
  audienceLabel: "Business Teams",
  description: "A voice AI genie that knows your business and talks to your customers.",
  seoDescription:
    "Help Genie for Business. Create a branded AI voice agent that knows your products, answers calls, chats on your website, captures leads, and works 24/7.",
  heroHeadline: "Never miss a",
  heroHighlightWord: "customer",
  heroRotatingWords: ["customer", "lead", "sale", "opportunity"],
  heroSubheadline:
    "Create a voice AI genie that knows your products, answers customer calls, chats on your website, captures leads, and works around the clock, set up in minutes, not months.",
  primaryCTA: {
    label: "Start Free",
    href: "/signup",
  },
  secondaryCTA: {
    label: "Book a Demo",
    href: "/contact",
  },
  capabilityTitle: "Everything your genie needs to represent your business",
  capabilitySubtitle:
    "Upload your docs, customize the personality, deploy everywhere, and review every conversation.",
  capabilities: [
    {
      title: "Your Own AI Voice Agent",
      description:
        "Create a branded voice genie that knows your products, services, and policies. It answers calls, chats on your website, and speaks with your brand's personality.",
      image: "/segment-pages/business/cap-omni-channel.webp",
    },
    {
      title: "Knowledge Base That Powers Every Answer",
      description:
        "Upload product manuals, FAQs, pricing docs, and policies. Your genie learns it all and gives accurate, grounded answers, no scripting required.",
      image: "/segment-pages/business/cap-qualification.webp",
    },
    {
      title: "Every Channel, One Genie",
      description:
        "Deploy on your website as a chat widget, connect a dedicated phone number, share a link, or embed in your app, same genie, same knowledge, everywhere.",
      image: "/segment-pages/business/cap-routing.webp",
    },
    {
      title: "Conversations You Can Actually Review",
      description:
        "Read full transcripts, see exactly what customers asked, track lead captures, and use built-in analytics to improve, it's not a black box.",
      image: "/segment-pages/business/cap-insights.webp",
    },
  ],
  useCaseTitle: "How businesses use Help Genie today",
  useCaseSubtitle:
    "Real use cases delivering measurable results for small and mid-size businesses.",
  useCases: [
    {
      title: "After-Hours Sales Coverage",
      description:
        "Your genie handles calls and chats when you're closed, answering product questions, capturing contact details, and sending you a lead summary by morning.",
      bullets: [
        "24/7 voice and chat availability",
        "Automatic lead capture with contact details",
        "Morning summary with every conversation",
      ],
      image: "/segment-pages/business/usecase-lead-capture.webp",
    },
    {
      title: "Product Support Without the Wait",
      description:
        "Customers get instant answers about warranty, troubleshooting, specs, and returns, straight from your uploaded docs, any time of day.",
      bullets: [
        "Answers grounded in your actual documentation",
        "Handles repeat questions so your team doesn't",
        "Escalates to a human when it needs to",
      ],
      image: "/segment-pages/business/usecase-support.webp",
    },
    {
      title: "Appointment & Intake Automation",
      description:
        "Your genie qualifies prospects, collects intake information through natural conversation, and guides them to the right next step, no forms, no friction.",
      bullets: [
        "Conversational intake replaces static forms",
        "Qualification logic built into the flow",
        "Booked appointments with full context attached",
      ],
      image: "/segment-pages/business/usecase-intake.webp",
    },
  ],
  processTitle: "How it works",
  processSubtitle: "From signup to live in three steps, no developers needed.",
  processSteps: [
    {
      title: "Upload your docs",
      description:
        "Add product manuals, FAQs, pricing sheets, and policies to your knowledge base. Your genie learns from them instantly.",
    },
    {
      title: "Customize your genie",
      description:
        "Set the voice, personality, greeting, and conversation rules. Choose what it should handle vs. escalate to your team.",
    },
    {
      title: "Go live everywhere",
      description:
        "Deploy on your website, connect a phone number, share a link, then review conversations and leads from your dashboard.",
    },
  ],
  pillars: [
    {
      title: "Voice\n+ Chat",
      description: "One genie that handles both calls and website conversations.",
      gradient: "from-purple-400 to-blue-400",
    },
    {
      title: "Your\nKnowledge",
      description: "Every answer grounded in your actual docs and policies.",
      gradient: "from-blue-400 to-indigo-400",
    },
    {
      title: "Your\nBrand",
      description: "Custom voice, personality, and messaging that sounds like you.",
      gradient: "from-indigo-400 to-purple-400",
    },
  ],
  finalCTAHeadline: "Your business, always available",
  finalCTACopy:
    "Give every customer a fast, knowledgeable experience, whether they call at 2 PM or 2 AM. Your genie never sleeps, never forgets, and always represents your brand.",
  finalPrimaryCTA: {
    label: "Get Started Free",
    href: "/signup",
  },
  finalSecondaryCTA: {
    label: "See Pricing",
    href: "/pricing",
  },
  imagery: {
    heroImage: "/segment-pages/business/hero.webp",
    ctaBackgroundImage: "/segment-pages/business/cta-background.webp",
  },
};

export const DEVELOPERS_SEGMENT_PAGE: SegmentPageContent = {
  slug: "developers",
  title: "Help Genie for Developers",
  audienceLabel: "Developers",
  description: "One API. 32 resources. Voice and chat, shipped from your stack.",
  seoDescription:
    "Help Genie for Developers. A unified REST API with 32 resources, TypeScript SDK, webhooks, and embeddable widgets to build voice and chat products on your stack.",
  heroHeadline: "Ship voice AI",
  heroHighlightWord: "faster",
  heroRotatingWords: ["faster", "everywhere", "your way", "today"],
  heroSuffix: "",
  heroSubheadline:
    "Help Genie gives you a complete developer platform to add voice and chat agents to any product. One REST API, a typed TypeScript SDK, real-time webhooks, and embeddable widgets, ready to ship.",
  primaryCTA: {
    label: "Start Building",
    href: "/signup",
  },
  secondaryCTA: {
    label: "View API Docs",
    href: "https://docs.helpgenie.ai",
  },
  capabilityTitle: "Everything you need to build",
  capabilitySubtitle:
    "A complete developer platform, not just an API. Build, embed, and monitor conversational experiences from your own codebase.",
  capabilities: [
    {
      title: "32 Resources, One REST API",
      description:
        "Genies, conversations, leads, knowledge bases, analytics, voices, playbooks, and more, all through a single, consistent REST API with typed responses.",
      image: "/segment-pages/developers/cap-api-contracts.webp",
    },
    {
      title: "Webhooks & Real-time Events",
      description:
        "Subscribe to conversation events, lead captures, and genie state changes. Push data into your systems the moment it happens, no polling required.",
      image: "/segment-pages/developers/cap-integrations.webp",
    },
    {
      title: "Embed Anywhere",
      description:
        "Drop a voice widget into any web app, connect a phone number via API, or build fully custom UIs using the conversation endpoints. Your front-end, our engine.",
      image: "/segment-pages/developers/cap-environments.webp",
    },
    {
      title: "Full Genie Lifecycle via API",
      description:
        "Create, configure, update, and deploy genies programmatically. Manage knowledge bases, voices, playbooks, and analytics, all through code.",
      image: "/segment-pages/developers/cap-iteration.webp",
    },
  ],
  useCaseTitle: "What developers are building",
  useCaseSubtitle:
    "Real implementations shipping on the Help Genie platform today.",
  useCases: [
    {
      title: "Voice-Enabled Product Support",
      description:
        "Build voice genies that know your product docs, handle customer questions 24/7, and push conversation summaries to your ticketing system.",
      bullets: [
        "Upload docs via Knowledge Base API",
        "Real-time conversation webhooks",
        "Auto-generated lead records per caller",
      ],
      image: "/segment-pages/developers/usecase-helpdesk.webp",
    },
    {
      title: "Conversational Lead Capture",
      description:
        "Deploy genies on landing pages that qualify leads through natural conversation, then sync contacts and engagement scores to your CRM automatically.",
      bullets: [
        "Embeddable widget with 3 lines of code",
        "Lead webhook with full transcript",
        "Custom field mapping to any CRM",
      ],
      image: "/segment-pages/developers/usecase-crm.webp",
    },
    {
      title: "White-Label Consumer Apps",
      description:
        "Use the API to build branded mobile or web experiences where end users interact with genies you've configured, just like the Help Genie consumer app.",
      bullets: [
        "Genie Groups API for multi-tenant setups",
        "Consumer-facing conversation endpoints",
        "Marketplace API for discoverability",
      ],
      image: "/segment-pages/developers/usecase-internal.webp",
    },
  ],
  processTitle: "Build flow",
  processSubtitle: "From API key to production in three steps.",
  processSteps: [
    {
      title: "Get your API key",
      description:
        "Sign up, generate keys from the developer dashboard, and make your first API call in minutes.",
    },
    {
      title: "Build your integration",
      description:
        "Use the TypeScript SDK or call the REST API directly to create genies, upload knowledge, and manage conversations.",
    },
    {
      title: "Ship to your users",
      description:
        "Embed widgets, connect phone numbers, or build custom UIs, then monitor everything with built-in analytics.",
    },
  ],
  pillars: [
    {
      title: "REST\nAPI",
      description: "32 resources with consistent, typed JSON contracts.",
      gradient: "from-purple-400 to-blue-400",
    },
    {
      title: "TypeScript\nSDK",
      description: "Typed client with full IntelliSense for every endpoint.",
      gradient: "from-blue-400 to-indigo-400",
    },
    {
      title: "Webhook\nEvents",
      description: "Real-time pushes for conversations, leads, and state changes.",
      gradient: "from-indigo-400 to-purple-400",
    },
  ],
  finalCTAHeadline: "Your logic. Our conversation engine.",
  finalCTACopy:
    "Keep your business logic in your services. Use Help Genie as the voice and chat layer, deployed through code, monitored through analytics.",
  finalPrimaryCTA: {
    label: "Get Your API Key",
    href: "/signup",
  },
  finalSecondaryCTA: {
    label: "Talk to Our Team",
    href: "/contact",
  },
  imagery: {
    heroImage: "/segment-pages/developers/hero.webp",
    ctaBackgroundImage: "/segment-pages/developers/cta-background.webp",
  },
};

export const PARTNERS_SEGMENT_PAGE: SegmentPageContent = {
  slug: "partners",
  title: "Help Genie for Partners",
  audienceLabel: "Agencies & Partners",
  description: "Sell voice AI to your clients. Their logo, colours and voice on every genie. Recurring revenue.",
  seoDescription:
    "Help Genie for Partners. Refer clients or set up genies for them, each with the client's own logo, colours and voice. Co-branded onboarding, several genies per account, and referral revenue from voice and chat solutions.",
  heroHeadline: "Offer every client",
  heroHighlightWord: "voice AI",
  heroRotatingWords: ["voice AI", "24/7 support", "a phone agent", "chat + voice"],
  heroSuffix: "",
  heroSubheadline:
    "Give your clients a genie that answers their calls, chats on their website, and captures leads, with their own logo, colours and voice. You sell the solution, we power the platform.",
  primaryCTA: {
    label: "Become a Partner",
    href: "/partners",
  },
  secondaryCTA: {
    label: "Apply to Partner",
    href: "/partners/apply",
  },
  capabilityTitle: "Everything you need to push voice AI to your clients",
  capabilitySubtitle:
    "Voice and chat genies you can set up and brand for each client, with several genies in one account.",
  capabilities: [
    {
      title: "Your Client's Brand on Every Genie",
      description:
        "Set up voice and chat genies that sound and feel like your client's brand: their logo, colours, custom voice, personality, greeting, and knowledge base.",
      image: "/segment-pages/partners/cap-launch-faster.webp",
    },
    {
      title: "Every Client in One Account",
      description:
        "Run a genie for each client from one Help Genie account. Each genie has its own knowledge base, voice, phone number, conversations, and leads.",
      image: "/segment-pages/partners/cap-client-value.webp",
    },
    {
      title: "Recurring Revenue Per Client",
      description:
        "Bundle voice AI setup, ongoing management, and dedicated phone numbers into monthly retainers. You set the pricing, we power the platform, your margin, your model.",
      image: "/segment-pages/partners/cap-revenue.webp",
    },
    {
      title: "Per-Client Voice Analytics",
      description:
        "Show clients exactly what their genie is handling, calls answered, conversations managed, leads captured, and questions resolved. Real data you can share with them.",
      image: "/segment-pages/partners/cap-enterprise.webp",
    },
  ],
  useCaseTitle: "How partners sell voice AI with Help Genie",
  useCaseSubtitle:
    "Real partner models generating recurring revenue from voice and chat solutions today.",
  useCases: [
    {
      title: "Managed Voice AI Services",
      description:
        "Set up and manage AI voice agents for your client portfolio, uploading their docs, tuning the voice, connecting phone numbers, and reviewing conversations monthly. You charge the retainer, we handle the infrastructure.",
      bullets: [
        "Every client's genie in one account",
        "Monthly call and conversation reporting per client",
        "You set the price, keep the margin",
      ],
      image: "/segment-pages/partners/usecase-managed.webp",
    },
    {
      title: "Industry-Specific Voice Rollouts",
      description:
        "Build a voice AI template for a specific industry, trades, automotive, hospitality, healthcare, then roll it out to multiple clients with per-client customization on top.",
      bullets: [
        "Pre-built industry voice playbooks and prompts",
        "Clone and customize per client",
        "Measure call volume and lead capture improvements",
      ],
      image: "/segment-pages/partners/usecase-transformation.webp",
    },
    {
      title: "Embed Voice AI in Your Platform",
      description:
        "Embed a genie's voice and chat widget in your existing product or client portal. Each client can have its own genie with its own logo, colours, and phone number.",
      bullets: [
        "Embeddable voice and chat widget with your logo and colours",
        "Conversations and leads available through the API",
        "Usage-based or flat-rate billing flexibility",
      ],
      image: "/segment-pages/partners/usecase-platform.webp",
    },
  ],
  processTitle: "How the partnership works",
  processSubtitle: "From first call to selling voice AI in three steps.",
  processSteps: [
    {
      title: "Pick your model",
      description:
        "Choose how you want to sell voice AI, managed services, industry rollouts, or platform embedding. We help you scope the commercial model.",
    },
    {
      title: "Launch your first clients",
      description:
        "We onboard your first 2-3 clients together, configuring voice agents, uploading knowledge, connecting phone numbers. You learn the platform, we ensure success.",
    },
    {
      title: "Scale and earn",
      description:
        "Add a genie for each new client, start from a ready-made industry genie, and grow your recurring revenue. We provide ongoing support and co-selling where it helps.",
    },
  ],
  pillars: [
    {
      title: "Voice\nAI",
      description: "AI agents that answer calls and chat, powered by you.",
      gradient: "from-purple-400 to-blue-400",
    },
    {
      title: "Client\nBrand",
      description: "Their logo, colours and voice on every genie and widget.",
      gradient: "from-blue-400 to-indigo-400",
    },
    {
      title: "Your\nRevenue",
      description: "You set the price. We power the platform. You keep the margin.",
      gradient: "from-indigo-400 to-purple-400",
    },
  ],
  finalCTAHeadline: "Your clients need voice AI. You deliver it.",
  finalCTACopy:
    "Earn recurring revenue by bringing voice and chat AI to your clients. Branded genies, dedicated phone numbers, co-branded onboarding, and a referral program.",
  finalPrimaryCTA: {
    label: "Apply to the Partner Program",
    href: "/partners/apply",
  },
  finalSecondaryCTA: {
    label: "Explore Industry Solutions",
    href: "/help-genie-for-business-solutions",
  },
  imagery: {
    heroImage: "/segment-pages/partners/hero.webp",
    ctaBackgroundImage: "/segment-pages/partners/cta-background.webp",
  },
};

export const CONSUMERS_SEGMENT_PAGE: SegmentPageContent = {
  slug: "consumers",
  title: "Help Genie for Consumers",
  audienceLabel: "Consumers",
  description: "Your personal genie experience in one mobile app.",
  seoDescription:
    "Help Genie for Consumers gives people one mobile app to organize their assets, discover marketplace experiences, and build custom genie collections. Coming soon.",
  heroHeadline: "Your own",
  heroHighlightWord: "voice agent",
  heroRotatingWords: ["voice agent", "home expert", "product guide", "smart helper"],
  heroSuffix: "",
  heroSubheadline:
    "Create personal voice agents that know your home, your products, and your life, or talk to agents from businesses you already use. All in one app.",
  primaryCTA: {
    label: "Join the Waitlist",
    href: "/signup",
  },
  secondaryCTA: {
    label: "See How It Works",
    href: "/how-it-works",
  },
  capabilityTitle: "Built for everyday consumer value",
  capabilitySubtitle:
    "A personal experience that organizes your world and grows with you.",
  capabilities: [
    {
      title: "Unified Asset Hub",
      description:
        "Bring personal assets together in one secure, organized mobile experience, always accessible.",
      image: "/segment-pages/consumers/cap-asset-hub.webp",
    },
    {
      title: "Marketplace Discovery",
      description:
        "Discover useful marketplace experiences tailored to what matters most to you and your lifestyle.",
      image: "/segment-pages/consumers/cap-marketplace.webp",
    },
    {
      title: "Custom Collections",
      description:
        "Create and manage your own custom genie collections for different needs, travel, home, shopping, and more.",
      image: "/segment-pages/consumers/cap-collections.webp",
    },
    {
      title: "Grows With You",
      description:
        "Access your information and conversations in one place as your collection grows and evolves.",
      image: "/segment-pages/consumers/cap-grows.webp",
    },
  ],
  useCaseTitle: "What consumers will be able to do",
  useCaseSubtitle:
    "A few of the ways the consumer app will simplify your daily life.",
  useCases: [
    {
      title: "Asset Organization",
      description:
        "Organize household and personal assets so answers are easier to find when needed.",
      bullets: [
        "Document storage and retrieval",
        "Smart categorization",
        "Instant voice-powered search",
      ],
      image: "/segment-pages/consumers/usecase-assets.webp",
    },
    {
      title: "Lifestyle Collections",
      description:
        "Use focused genie collections for travel, home, shopping, and day-to-day planning.",
      bullets: [
        "Pre-built lifestyle templates",
        "Personalized recommendations",
        "Shareable with family members",
      ],
      image: "/segment-pages/consumers/usecase-lifestyle.webp",
    },
    {
      title: "Marketplace Exploration",
      description:
        "Explore marketplace options and add new experiences without rebuilding from scratch.",
      bullets: [
        "One-tap experience additions",
        "Ratings and reviews from others",
        "Try before you commit",
      ],
      image: "/segment-pages/consumers/usecase-marketplace.webp",
    },
  ],
  processTitle: "How consumers get started",
  processSubtitle: "Simple steps to your personal genie experience.",
  processSteps: [
    {
      title: "Join early access",
      description:
        "Sign up now to be notified as soon as the app is available for download.",
    },
    {
      title: "Set up your collections",
      description:
        "Create custom genie collections based on your lifestyle and priorities.",
    },
    {
      title: "Use it daily",
      description:
        "Manage assets, discover new marketplace experiences, and keep everything in sync.",
    },
  ],
  pillars: [
    {
      title: "Your\nAssets",
      description: "Everything organized in one secure, personal space.",
      gradient: "from-purple-400 to-blue-400",
    },
    {
      title: "Your\nCollections",
      description: "Custom genie experiences built around your lifestyle.",
      gradient: "from-blue-400 to-indigo-400",
    },
    {
      title: "Your\nMarketplace",
      description: "Discover and add new experiences whenever you want.",
      gradient: "from-indigo-400 to-purple-400",
    },
  ],
  finalCTAHeadline: "The app is coming very soon",
  finalCTACopy:
    "Get ready for a consumer experience built around your assets and your own custom genie collections.",
  finalPrimaryCTA: {
    label: "Get Early Access",
    href: "/signup",
  },
  finalSecondaryCTA: {
    label: "Contact Us",
    href: "/contact",
  },
  imagery: {
    heroImage: "/segment-pages/consumers/hero.webp",
    ctaBackgroundImage: "/segment-pages/consumers/cta-background.webp",
  },
};
